import React, { useState } from 'react';
import './PersonalDataStep.css';
import { getPublicAssetUrl } from '../../utils/publicAssets';
import {
    CustomerInfoFields,
    isPersonalDataFormValid,
} from '../../utils/personalDataValidation';

interface PersonalDataStepProps {
    pay: (event: React.FormEvent<HTMLFormElement>) => void;
    customerInfo: CustomerInfoFields;
    setCustomerInfo: React.Dispatch<React.SetStateAction<CustomerInfoFields>>;
    termsAccepted: boolean;
    setTermsAccepted: React.Dispatch<React.SetStateAction<boolean>>;
    marketingAccepted: boolean;
    setMarketingAccepted: React.Dispatch<React.SetStateAction<boolean>>;
}

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isFieldValid = (name: keyof CustomerInfoFields, value: string): boolean => {
    const trimmed = value.trim();
    if (!trimmed) {
        return false;
    }
    if (name === 'email') {
        return emailPattern.test(trimmed);
    }
    if (name === 'additionalInfo') {
        return false;
    }
    return true;
};

const UserIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
        <circle cx="12" cy="8" r="3.2" />
        <path d="M5.5 19.2c1.6-3.2 4-4.8 6.5-4.8s4.9 1.6 6.5 4.8" />
    </svg>
);

const InfoIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
        <circle cx="12" cy="12" r="8.2" />
        <path d="M12 11v5.2M12 8.2v.2" strokeLinecap="round" />
    </svg>
);

const ShieldLockIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
        <path d="M12 3l7 3v5.5c0 4.4-2.9 7.8-7 9.5-4.1-1.7-7-5.1-7-9.5V6l7-3z" />
        <rect x="9.2" y="10.2" width="5.6" height="4.4" rx="1" />
        <path d="M12 10.2V9a1.4 1.4 0 0 1 2.8 0v1.2" />
    </svg>
);

const CheckIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="10" fill="#2e7d32" />
        <path d="M7.8 12.2l2.8 2.8 5.6-5.8" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const RequiredMark = () => (
    <span className="personal-data-required-mark" aria-hidden="true">
        *
    </span>
);

const PersonalDataStep: React.FC<PersonalDataStepProps> = ({
    pay,
    customerInfo,
    setCustomerInfo,
    termsAccepted,
    setTermsAccepted,
    marketingAccepted,
    setMarketingAccepted,
}) => {
    const [showErrors, setShowErrors] = useState(false);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setCustomerInfo((prev) => ({ ...prev, [name]: value }));
    };

    const focusFirstInvalidField = () => {
        const requiredOrder: Array<keyof CustomerInfoFields> = [
            'name',
            'surname',
            'email',
            'phone',
            'postcode',
            'gettoknow',
        ];

        for (const name of requiredOrder) {
            if (!isFieldValid(name, customerInfo[name])) {
                const el = document.querySelector<HTMLElement>(
                    `#booking-payment-form [name="${name}"]`
                );
                el?.focus();
                el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                return;
            }
        }

        if (!termsAccepted) {
            const el = document.querySelector<HTMLElement>('#booking-payment-form [name="terms"]');
            el?.focus();
            el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        setShowErrors(true);

        if (!isPersonalDataFormValid(customerInfo, termsAccepted)) {
            event.preventDefault();
            // Wait a tick so error styles paint before scrolling.
            window.requestAnimationFrame(() => focusFirstInvalidField());
            return;
        }

        pay(event);
    };

    const renderField = (
        name: keyof Pick<
            CustomerInfoFields,
            'name' | 'surname' | 'email' | 'phone' | 'postcode' | 'gettoknow'
        >,
        label: string,
        type: string,
        options?: {
            autoComplete?: string;
            inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
            placeholder?: string;
        }
    ) => {
        const value = customerInfo[name];
        const valid = isFieldValid(name, value);
        const showFieldError = showErrors && !valid;
        const errorMessage =
            name === 'email' && value.trim() && !emailPattern.test(value.trim())
                ? 'Introduce un email válido'
                : 'Campo obligatorio';

        return (
            <label
                className={[
                    'personal-data-field',
                    valid ? 'personal-data-field--valid' : '',
                    showFieldError ? 'personal-data-field--error' : '',
                ]
                    .filter(Boolean)
                    .join(' ')}
            >
                <span className="personal-data-field-label">
                    {label}
                    <RequiredMark />
                </span>
                <span className="personal-data-field-control">
                    <input
                        type={type}
                        name={name}
                        value={value}
                        required
                        aria-required="true"
                        aria-invalid={showFieldError || undefined}
                        onChange={handleInputChange}
                        autoComplete={options?.autoComplete}
                        inputMode={options?.inputMode}
                        placeholder={options?.placeholder}
                    />
                    {valid && (
                        <span className="personal-data-field-check" aria-hidden="true">
                            <CheckIcon />
                        </span>
                    )}
                </span>
                {showFieldError && (
                    <span className="personal-data-field-error">{errorMessage}</span>
                )}
            </label>
        );
    };

    const formValid = isPersonalDataFormValid(customerInfo, termsAccepted);
    const termsError = showErrors && !termsAccepted;
    const showErrorBanner = showErrors && !formValid;

    return (
        <div className="personal-data-step">
            <form
                id="booking-payment-form"
                className={`personal-data-form${showErrors ? ' personal-data-form--attempted' : ''}`}
                onSubmit={handleSubmit}
                noValidate
            >
                <p id="personal-data-required-hint" className="personal-data-required-legend">
                    Los campos marcados con <RequiredMark /> son obligatorios
                </p>

                {showErrorBanner && (
                    <div className="personal-data-error-banner" role="alert">
                        Revisa los campos obligatorios para poder continuar con el pago.
                    </div>
                )}

                <section className="personal-data-card" aria-labelledby="personal-data-contact-title">
                    <header className="personal-data-card-header">
                        <span className="personal-data-card-icon" aria-hidden="true">
                            <UserIcon />
                        </span>
                        <h3 id="personal-data-contact-title">Datos de contacto</h3>
                    </header>

                    <div className="personal-data-grid">
                        {renderField('name', 'Nombre', 'text', { autoComplete: 'given-name' })}
                        {renderField('surname', 'Apellidos', 'text', { autoComplete: 'family-name' })}
                        {renderField('email', 'Email', 'email', { autoComplete: 'email' })}
                        {renderField('phone', 'Teléfono', 'tel', {
                            autoComplete: 'tel',
                            inputMode: 'tel',
                        })}
                    </div>
                </section>

                <section className="personal-data-card" aria-labelledby="personal-data-extra-title">
                    <header className="personal-data-card-header">
                        <span className="personal-data-card-icon" aria-hidden="true">
                            <InfoIcon />
                        </span>
                        <h3 id="personal-data-extra-title">Información adicional</h3>
                    </header>

                    <div className="personal-data-grid personal-data-grid--additional">
                        {renderField('postcode', 'Código Postal', 'text', {
                            autoComplete: 'postal-code',
                            inputMode: 'numeric',
                        })}
                        {renderField('gettoknow', '¿Cómo nos conociste?', 'text', {
                            placeholder: 'Redes sociales, recomendación…',
                        })}

                        <label className="personal-data-field personal-data-field--full">
                            <span className="personal-data-field-label">
                                ¿Quieres comentarnos algo?{' '}
                                <span className="personal-data-optional-mark">(opcional)</span>
                            </span>
                            <textarea
                                name="additionalInfo"
                                value={customerInfo.additionalInfo}
                                onChange={handleInputChange}
                                rows={3}
                                placeholder="Escribe aquí tu comentario"
                            />
                        </label>
                    </div>
                </section>

                <aside className="personal-data-secure" aria-label="Pago seguro">
                    <span className="personal-data-secure-icon" aria-hidden="true">
                        <ShieldLockIcon />
                    </span>
                    <div className="personal-data-secure-copy">
                        <strong>Pago 100% seguro</strong>
                        <p>
                            Realizarás ahora únicamente el pago de la reserva. El importe restante se
                            abonará el día del evento en el centro.
                        </p>
                    </div>
                    <img
                        className="personal-data-secure-cards"
                        src={getPublicAssetUrl('img/tarjetas.png')}
                        alt="Visa, Mastercard, Maestro, American Express y Diners Club"
                    />
                </aside>

                <div className="personal-data-consents">
                    <label
                        className={`personal-data-consent${termsError ? ' personal-data-consent--error' : ''}`}
                    >
                        <input
                            type="checkbox"
                            name="terms"
                            checked={termsAccepted}
                            onChange={() => setTermsAccepted(!termsAccepted)}
                            required
                            aria-required="true"
                            aria-invalid={termsError || undefined}
                        />
                        <span>
                            He leído y acepto los{' '}
                            <a
                                href="http://www.hankyplanet.com/terminos-y-condiciones-de-uso/"
                                target="_blank"
                                rel="noreferrer"
                            >
                                términos y condiciones
                            </a>
                            <RequiredMark />
                        </span>
                    </label>
                    {termsError && (
                        <span className="personal-data-field-error personal-data-field-error--consent">
                            Debes aceptar los términos y condiciones
                        </span>
                    )}

                    <label className="personal-data-consent">
                        <input
                            type="checkbox"
                            name="marketing"
                            checked={marketingAccepted}
                            onChange={() => setMarketingAccepted(!marketingAccepted)}
                        />
                        <span>
                            Deseo recibir comunicaciones comerciales y ofertas. Leer{' '}
                            <a
                                href="https://www.hankyplanet.com/politica-de-privacidad/"
                                target="_blank"
                                rel="noreferrer"
                            >
                                política de privacidad
                            </a>{' '}
                            <span className="personal-data-optional-mark">(opcional)</span>
                        </span>
                    </label>
                </div>
            </form>
        </div>
    );
};

export default PersonalDataStep;
